import { RiPushpin2Fill, RiPushpinLine } from '@remixicon/react'
import { useDecision } from '@optimizely/react-sdk'
import { useUser } from 'src/hook/useUser'
import { usePermission } from 'src/pages/CommunityDetails/Posts/PostItem/MenuOwner/hooks/usePermissions'
import { AuthStatus } from '@hotmart/space-lib/dist/enum'
import { usePinPost } from 'src/pages/CommunityDetails/Posts/PinPost'
import { FeatureToggle, useFeatureToggle } from 'src/hook/useFeatureToggle'
import { PermissionActions } from 'src/services/community'
import { NewsPostType } from 'src/hook/usePosts'
import { filterPermission } from 'src/utils/user'
import { FilterPermissionParams } from 'src/utils/user/types'
import { ManagerRoles } from 'src/hook/useUser/constants'
import { LiveStatus } from 'src/pages/CommunityDetails/Posts/live'
import { useCommunityDetails } from 'src/pages/CommunityDetails/Posts/context/communityDetailsContext'
import i18n from 'src/i18n'
import { FeedCardHeaderDropdown } from '../feedCardHeaderDropdown'
import { FeedCardHeaderActionsProps } from './feedCardHeaderActions.types'
import { ActionButton } from './feedCardHeaderActions.styles'

const FeedCardHeaderActions = ({ post, isScheduledPost }: FeedCardHeaderActionsProps) => {
  const { community } = useCommunityDetails()
  const { isOwnerOrAdminOrModerator, permissionsUser, authStatus } = useUser()
  const { canDeletePost, canPinPost, canUnPinPost, canPublishScheduledPost, hasPermissionToBan } =
    usePermission({ post })
  const { handlePinPost, handleUnpinPost } = usePinPost({ postId: post.id })
  const [{ enabled: enabledEditPostFeatureFlag }] = useDecision('com_adm_web_enable_edit_post')
  const [{ enabled: enableCommunityWebinar }] = useDecision('com_adm_web_enable_community_webinar')
  const isBanMemberActive = useFeatureToggle(FeatureToggle.communitiesBanMember)

  const showPostActions =
    !post?.membershipInfo?.courseTag &&
    (post.isAuthor || isOwnerOrAdminOrModerator) &&
    (community.ownerId !== post.authorId ||
      permissionsUser?.group === ManagerRoles.OWNER ||
      permissionsUser?.group === ManagerRoles.MODERATOR)

  const hasPermissionToViewReport = filterPermission({
    specificPermission: PermissionActions.VIEW_LIVE_REPORT,
    permissionsUserList: (community.permissions?.actions ??
      []) as unknown as FilterPermissionParams['permissionsUserList']
  })

  const canPinUnpinPost = authStatus === AuthStatus.AUTHENTICATED && (canPinPost || canUnPinPost)
  const canBlockComments = isOwnerOrAdminOrModerator
  const canEditPost = enabledEditPostFeatureFlag
  const canBanUser = isBanMemberActive && hasPermissionToBan
  const canViewLiveReport =
    enableCommunityWebinar &&
    hasPermissionToViewReport &&
    post?.postType === NewsPostType.LIVE &&
    post?.live?.status === LiveStatus.COMPLETED

  const handlePinUnpinPost = () => {
    if (post.isFixed) {
      handleUnpinPost()
      return
    }

    handlePinPost()
  }

  if (!showPostActions) {
    return null
  }

  return (
    <div className="_d-flex _align-items-center _ml-auto">
      {canPinUnpinPost && (
        <ActionButton
          className="_d-flex _align-items-center _justify-content-center"
          onClick={handlePinUnpinPost}
        >
          {post.isFixed ? (
            <RiPushpin2Fill size={20} className="_text-blue-light" />
          ) : (
            <RiPushpinLine size={20} className="_text-gray-900" />
          )}

          <span className="_sr-only">
            {post?.isFixed ? i18n.t('pin_post.unpin_button') : i18n.t('pin_post.pin_button')}
          </span>
        </ActionButton>
      )}

      <FeedCardHeaderDropdown
        post={post}
        canPinUnpinPost={canPinUnpinPost}
        canBlockComments={canBlockComments}
        canBanUser={canBanUser}
        canDeletePost={canDeletePost}
        canEditPost={canEditPost}
        canViewLiveReport={!!canViewLiveReport}
        canPublishScheduledPost={!!canPublishScheduledPost && isScheduledPost}
        onPinUnpinPost={handlePinUnpinPost}
      />
    </div>
  )
}

export default FeedCardHeaderActions
